export type RuntimeConfig = {
  apiBaseUrl?: string;
  appVersion?: string;
  /** Azure Application Insights connection string. When set, browser telemetry is sent to the
   *  shared resource; empty/absent keeps telemetry off (e.g. local dev). Write-only ingestion,
   *  safe to expose to the browser. */
  appInsightsConnectionString?: string;
  /** When true, the UI shows a persistent SANDBOX mark. Set only on test hosts via config.json / ML_SANDBOX — never from a copied database. */
  sandbox?: boolean;
};

const defaultRuntimeConfig: RuntimeConfig = {
  apiBaseUrl: "",
};

let cachedConfig: RuntimeConfig = defaultRuntimeConfig;

const normalizeBaseUrl = (value?: string) => {
  if (!value) {
    return "";
  }
  return value.replace(/\/+$/, "");
};

const parseSandbox = (value: unknown): boolean => value === true || value === "true";

export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (window.__ML_RUNTIME_CONFIG__) {
    cachedConfig = {
      ...defaultRuntimeConfig,
      ...window.__ML_RUNTIME_CONFIG__,
    };
    cachedConfig.apiBaseUrl = normalizeBaseUrl(cachedConfig.apiBaseUrl);
    cachedConfig.sandbox = parseSandbox(cachedConfig.sandbox);
    return cachedConfig;
  }

  try {
    const configUrl = new URL("config.json", `${window.location.origin}/`);
    const response = await fetch(configUrl, { cache: "no-store" });
    if (!response.ok) {
      throw new Error("Failed to load runtime config.");
    }
    const data = (await response.json()) as RuntimeConfig & { sandbox?: unknown };
    cachedConfig = {
      ...defaultRuntimeConfig,
      ...data,
      apiBaseUrl: normalizeBaseUrl(data.apiBaseUrl),
      sandbox: parseSandbox(data.sandbox),
    };
  } catch {
    cachedConfig = defaultRuntimeConfig;
  }

  window.__ML_RUNTIME_CONFIG__ = cachedConfig;
  return cachedConfig;
};

export const getRuntimeConfig = (): RuntimeConfig => {
  return window.__ML_RUNTIME_CONFIG__ ?? cachedConfig;
};

export const getApiBaseUrl = (): string => {
  return normalizeBaseUrl(getRuntimeConfig().apiBaseUrl);
};

export const getAppInsightsConnectionString = (): string | undefined => {
  const value = getRuntimeConfig().appInsightsConnectionString;
  return value?.trim() ? value.trim() : undefined;
};

export const buildApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    return path;
  }
  return `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
};

export const resolveApiUrl = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return buildApiUrl(value);
};

export const getAppVersion = (): string | undefined => {
  const runtimeVersion = getRuntimeConfig().appVersion;
  if (runtimeVersion?.trim()) {
    return runtimeVersion.trim();
  }

  const envVersion = import.meta.env.VITE_APP_VERSION;
  if (envVersion?.trim()) {
    return envVersion.trim();
  }

  const commitSha = import.meta.env.VITE_GIT_SHA;
  if (commitSha?.trim()) {
    return commitSha.trim();
  }

  return undefined;
};

export const getIsSandbox = (): boolean => {
  return getRuntimeConfig().sandbox === true;
};
